import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../context/AuthContext";
import api from "../api";
import { Link, useLocation } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";

import {
  Box,
  Drawer,
  List,
  ListItemButton,
  Divider,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import { DRAWER_WIDTH, COLLAPSED_WIDTH } from "./layoutConstants";
import sidebarWaves from "../assets/sidebar-waves.svg";
import {
  SIDEBAR_GRADIENT,
  sidebarLabelSx,
  sidebarIconColor,
  sidebarRowSx,
  SIDEBAR_ITEM_MIN_HEIGHT,
  SIDEBAR_ITEM_GAP,
} from "../theme/sidebarBrand";

// Icon resolution -- sidebar_menus.icon is a Bootstrap Icons class string
// (the SAME "bi bi-*" convention already seeded for e.g. Technician's
// Review Forms row, "bi bi-clipboard-check" -- see
// database/phase-4c/008_seed_technician_review_forms_sidebar_unit1.sql).
// bootstrap-icons is the only icon font actually imported here (line 5) --
// no second icon library is introduced.
//
// Root cause of the "white circle" bug: menu.icon is used directly as an
// <i> className, and the previous fallback for a NULL/empty DB value was
// the literal string "fas fa-circle" -- a FONT AWESOME class, which this
// app never loads. An unrecognized class renders no glyph at all, leaving
// a blank inline box that reads as a plain circular dot.
//
// Fix: prefer the DB's own icon value when set (never overridden), else
// infer a real "bi bi-*" icon from the menu's path (works immediately for
// every currently-NULL row -- Complaints/My Tickets/Assign Forms/Review
// Forms/Home for Developer/Data-Analyst -- without requiring the matching
// manual DB update to be run first), else fall back to a subtle generic
// icon -- never the broken Font Awesome class, never a filled circle.
const PATH_ICON_MAP = {
  home: "bi bi-house-door",
  complaints: "bi bi-headset",
  "my-tasks": "bi bi-ticket-perforated",
  "tasks/create": "bi bi-plus-square",
  "tasks/raise-complaint": "bi bi-headset",
  "form-assignments": "bi bi-file-earmark-plus",
  "my-assigned-forms": "bi bi-clipboard-check",
  "my-work": "bi bi-kanban",
  "task-approval-policies": "bi bi-shield-check",
  "my-task-approvals": "bi bi-check2-square",
  "my-performance-dashboard": "bi bi-graph-up",
  performance: "bi bi-speedometer2",
  "kra-work": "bi bi-journal-check",
  reports: "bi bi-bar-chart",
};
const FALLBACK_ICON = "bi bi-grid";

function resolveMenuIcon(menu) {
  if (menu.icon) return menu.icon;
  const segments = String(menu.path || "").split("/").filter(Boolean);
  const key = segments.slice(1).join("/"); // drop the leading "/<role>" segment
  return PATH_ICON_MAP[key] || FALLBACK_ICON;
}

const StyledDrawer = styled(Drawer)(() => ({
  "& .MuiDrawer-paper": {
    border: 0,
    color: "#f7f0e6",
    overflowX: "hidden",
    overflowY: "hidden",
    // Satin-wave overlay (src/assets/sidebar-waves.svg) over the shared
    // maroon gradient (theme/sidebarBrand.js) -- also used verbatim by
    // AdminLayout/Sidebar.js so Manager/Technician/Admin share one sidebar.
    background: `url(${sidebarWaves}) left bottom / 100% auto no-repeat, ${SIDEBAR_GRADIENT}`,
    boxShadow: "8px 0 28px rgba(74, 14, 34, 0.22)",
    position: "fixed",

    // Soft light sheen behind the brand block, top-right.
    "&::before": {
      content: '""',
      position: "absolute",
      top: -90,
      right: -70,
      width: 220,
      height: 200,
      borderRadius: "50%",
      background: "radial-gradient(closest-side, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0))",
      pointerEvents: "none",
    },
  },
}));

// Root cause of the "click a sidebar item -> instantly land on the login
// page, looks exactly like a logout" bug: `menu.path` from sidebar_menus is
// handed straight to <Link to={...}> (below) with no normalization. Every
// nested role route in routes/AppRoutes.js is registered as a RELATIVE
// child path under its own role's parent route (e.g. `<Route path="/manager">
// <Route path="allocations/unit-wise" .../></Route>`) -- there is no
// top-level `/allocations/unit-wise` route at all. A sidebar_menus row
// entered as an ABSOLUTE path missing its role prefix (e.g.
// "/allocations/unit-wise" instead of "/manager/allocations/unit-wise", as
// opposed to a bare relative segment like "allocations/unit-wise", which a
// relative <Link> already resolves correctly under whatever section the
// user is currently in) matches NO route at all, so React Router falls
// through to the top-level catch-all (`<Route path="/*" element={<LoginPage
// />} />` in AppRoutes.js) -- the user's session/token never actually
// changes, but the UI looks identical to being logged out. This is a pure
// client-side routing bug, never an authentication/authorization one --
// see api.js's response interceptor (untouched, already correct: it only
// ever reacts to a genuine 401, never a 403) and the backend's own
// permission checks, neither of which this symptom has anything to do with.
//
// Resolving EVERY row the same way, regardless of how it happens to be
// stored, removes the entire class of bug rather than special-casing one
// role's one menu item: strip any leading slash, and if the first segment
// isn't already this section's own role prefix, add it.
function resolveMenuPath(rawPath, rolePrefix) {
  const segments = String(rawPath || "").split("/").filter(Boolean);
  if (!rolePrefix) return `/${segments.join("/")}`;
  if (!segments.length) return `/${rolePrefix}`;
  if (segments[0] === rolePrefix) return `/${segments.join("/")}`;
  return `/${rolePrefix}/${segments.join("/")}`;
}

const Sidebar = ({ isOpen, onClose }) => {
  const { user } = useContext(AuthContext);
  const [menus, setMenus] = useState([]);
  const location = useLocation();
  // Same "first URL segment is the role section" convention already used
  // elsewhere in this codebase (hooks/useReviewForms.js's useRolePrefix(),
  // components/forms/FormsHomeSummary.jsx) -- derived from the CURRENT,
  // already-matched route rather than from user.role_name, so it can never
  // drift out of sync with whichever section actually rendered this
  // sidebar.
  const rolePrefix = (location.pathname.match(/^\/([^/]+)\//) || [])[1] || "";

  // ThemeProvider issue avoid karne ke liye direct media query use karo
  const isDesktop = useMediaQuery("(min-width:900px)");

  const collapsed = isDesktop ? !isOpen : false;
  const drawerWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  useEffect(() => {
    if (user && user.role_id) {
      api
        .get(`/sidebar/by-role`, {
          params: {
            role_id: user.role_id,
            unit_id: user.unit_id || null,
          },
        })
        .then((res) => {
          setMenus(res.data.sort((a, b) => a.order_no - b.order_no));
        })
        .catch((err) => console.error("Sidebar fetch error:", err));
    }
  }, [user]);

  const handleMenuClick = () => {
    if (!isDesktop && onClose) {
      onClose();
    }
  };

  // Active pattern: gold left bar + a lighter translucent-white fill + white
  // text -- shared byte-for-byte with AdminLayout/Sidebar.js's activeStyles
  // via theme/sidebarBrand.js's sidebarRowSx/sidebarLabelSx/sidebarIconColor.
  const menuItemStyle = (active) => ({
    minHeight: SIDEBAR_ITEM_MIN_HEIGHT,
    px: collapsed ? 1.2 : 2.2,
    mx: 1.4,
    mb: 1,
    justifyContent: collapsed ? "center" : "flex-start",
    ...sidebarRowSx(active),
    textDecoration: "none",
    transition: "all 0.22s ease",

    "&:hover": {
      background: active ? "rgba(255, 255, 255, 0.17)" : "rgba(255, 255, 255, 0.07)",
      transform: isDesktop ? "translateX(2px)" : "none",
    },
  });

  const renderMenuItem = ({ to, icon, label, active }) => {
    return (
      <Tooltip
        key={to}
        title={collapsed ? label : ""}
        placement="right"
        arrow
        disableHoverListener={!collapsed}
      >
        <ListItemButton
          component={Link}
          to={to}
          onClick={handleMenuClick}
          sx={menuItemStyle(active)}
        >
          <Box
            component="i"
            className={icon}
            sx={{
              width: 22,
              minWidth: 22,
              height: 22,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              color: sidebarIconColor(active),
              mr: collapsed ? 0 : `${SIDEBAR_ITEM_GAP}px`,
            }}
          />

          {!collapsed && (
            <Typography component="span" sx={sidebarLabelSx(active)}>
              {label}
            </Typography>
          )}
        </ListItemButton>
      </Tooltip>
    );
  };

  return (
    <StyledDrawer
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop ? true : isOpen}
      onClose={onClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: {
          xs: 0,
          md: drawerWidth,
        },
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          maxWidth: "82vw",
          height: "100vh",
          boxSizing: "border-box",
          transition: "width 220ms ease",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "flex-start",
          gap: 1.6,
          px: collapsed ? 1 : 2.4,
          py: { xs: 1.8, sm: 2.6 },
          minHeight: { xs: 72, sm: 104 },
        }}
      >
        <Box
          sx={{
            width: collapsed ? 46 : 58,
            height: collapsed ? 46 : 58,
            borderRadius: "14px",
            background: "#fdfaf4",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 10px 24px rgba(0, 0, 0, 0.28)",
            flexShrink: 0,
            p: "5px",
          }}
        >
          {/* Emblem-only crop of the full wordmark logo (1405x717, emblem
              in its left ~36%): the image is scaled up and offset inside an
              overflow-hidden square so the tile shows just the emblem.
              Percentages keep the crop right at both tile sizes. */}
          <Box
            sx={{
              position: "relative",
              width: "100%",
              height: "100%",
              overflow: "hidden",
              borderRadius: "8px",
            }}
          >
            <Box
              component="img"
              src="/assets/brand/jaipuria-it360-logo-trimmed.jpg"
              alt="Jaipuria IT360 Desk"
              sx={{
                position: "absolute",
                width: "275%",
                maxWidth: "none",
                height: "auto",
                left: "-2.5%",
                top: "-38%",
                mixBlendMode: "multiply",
              }}
            />
          </Box>
        </Box>

        {!collapsed && (
          <Box>
            <Typography
              sx={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontWeight: 700,
                fontSize: 24,
                lineHeight: 1.05,
                letterSpacing: 0.2,
                color: "#ffffff",
              }}
            >
              Jaipuria
            </Typography>

            <Typography
              variant="caption"
              sx={{
                display: "block",
                mt: 0.5,
                color: "rgba(255, 245, 247, 0.88)",
                fontWeight: 700,
                letterSpacing: "0.2em",
                textTransform: "uppercase",
                fontSize: 11,
              }}
            >
              IT360 Desk
            </Typography>
          </Box>
        )}
      </Box>

      <Divider
        sx={{
          position: "relative",
          zIndex: 2,
          borderColor: alpha("#f7f0e6", 0.14),
          mb: 1.2,
        }}
      />

      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          px: 0,
          py: 1,
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          "&::-webkit-scrollbar": { width: 5 },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(200,154,63,0.35)",
            borderRadius: 10,
          },
        }}
      >
        <List disablePadding>
          {menus.map((menu) => {
            const to = resolveMenuPath(menu.path, rolePrefix);
            return renderMenuItem({
              to,
              icon: resolveMenuIcon(menu),
              label: menu.label,
              active: location.pathname === to,
            });
          })}
        </List>
      </Box>

    </StyledDrawer>
  );
};

export default Sidebar;